// One role, over the list (spec/07 §6.2–§6.4, F-IAM-11, F-ORG-13): what it may do, and the numbers
// its holders may not exceed. Permissions are shown as they are — a built-in role's are fixed, a
// custom role's are set where it is made; this screen edits the limits, which every role has. `null`
// is "no limit of the role's own": the setting alone applies, and the lower of the two always wins.
// The comp limit is its own choice (`CompLimitEditor`): the venue's setting, custom minutes, or — an
// owner's to give — no limit at all (owner decision 2026-09-30).

import type { Money, Role, RoleLimits } from '@clavian/api-client';
import { minorUnitDigits } from '@clavian/i18n';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Chip, Dialog, useToast } from '@clavian/ui-web';
import { useMemo, useState, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../../api/problems';
import { useAccess, useMyVenues } from '../../api/queries';
import { moneyFrom } from '../money';
import { InlineEdit, Notice, Section, SettingRow } from '../../ui';
import { useScopeIds } from '../../shell/scope';
import { CompLimitEditor, type CompValues } from './CompLimitEditor';
import { groupPermissions } from './permission-labels';
import { PermissionPicker } from './PermissionPicker';
import { useChangeRoleLimits, useDeleteRole, useUpdateRole } from './queries';
import { useProblem } from '../../api/use-problem';

const MEMBERS_ROLES = 'members:roles';

export function RoleDialog({
  role,
  catalogue,
  onClose,
}: {
  readonly role: Role | null;
  /** Every assignable permission (the built-in `owner` role's set), for the custom-role editor. */
  readonly catalogue: readonly string[];
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog
      open={role !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size="lg"
      eyebrow={t('staff.role.eyebrow')}
      title={role === null ? t('staff.role.title') : role.name}
    >
      {role !== null ? <RoleDetail role={role} catalogue={catalogue} onClose={onClose} /> : null}
    </Dialog>
  );
}

function RoleDetail({ role, catalogue, onClose }: { readonly role: Role; readonly catalogue: readonly string[]; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const scope = useScopeIds();
  const access = useAccess(scope.access);
  const venues = useMyVenues();
  const canEdit = access.status === 'ready' && access.can(MEMBERS_ROLES);
  // A built-in role's permissions are fixed in code (spec/07 §6.2); only a custom role's may be changed.
  const canEditPermissions = canEdit && !role.builtIn;

  // The currency a money limit is set in: the one already on the role, then the venue's, then the
  // one an organisation venue uses, then the deployment default. A deployment is one currency.
  const currency = useMemo(() => {
    const orgVenue = (venues.data ?? []).find((venue) => venue.organizationId === scope.organizationId && venue.currency !== undefined);
    return role.limits.refundApprovalAmount?.currency ?? role.limits.walletAdjustMaxAmount?.currency ?? scope.currency ?? orgVenue?.currency ?? 'INR';
  }, [role.limits, scope.currency, scope.organizationId, venues.data]);

  return (
    <div className="console-people-detail">
      <header className="console-role-head">
        {role.builtIn ? <Badge tone="neutral">{t('staff.roles.builtIn')}</Badge> : <Badge tone="accent">{t('staff.roles.custom')}</Badge>}
        <span className="console-subtle">{t('staff.roles.permissionCount', { count: role.permissions.length })}</span>
      </header>

      <PermissionsSection role={role} catalogue={catalogue} canEdit={canEditPermissions} />

      <LimitsEditor role={role} currency={currency} canEdit={canEdit} />

      {canEditPermissions ? <DeleteRoleSection role={role} onDeleted={onClose} /> : null}
    </div>
  );
}

/**
 * A role's permissions (spec/07 §6.2–§6.3). A built-in role shows them as read-only chips; a custom role
 * a holder of `members:roles` may edit — the same picker the create dialog uses, seeded from the role,
 * saved as the whole set (the endpoint replaces it). At least one permission must remain.
 */
function PermissionsSection({ role, catalogue, canEdit }: { readonly role: Role; readonly catalogue: readonly string[]; readonly canEdit: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const scope = useScopeIds();
  const update = useUpdateRole(scope.organizationId);
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set(role.permissions));
  const [error, setError] = useState<string | undefined>(undefined);

  function toggle(permission: string, on: boolean): void {
    setSelected((current) => {
      const next = new Set(current);
      if (on) next.add(permission);
      else next.delete(permission);
      return next;
    });
  }

  function start(): void {
    setSelected(new Set(role.permissions));
    setError(undefined);
    setEditing(true);
  }

  function save(): void {
    if (selected.size === 0) {
      setError(t('staff.role.edit.selectAtLeastOne'));
      return;
    }
    setError(undefined);
    update.mutate(
      { role: role.name, body: { permissions: [...selected] } },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('staff.role.edit.saved') });
          setEditing(false);
        },
        onError: (failure) => setError(problem.text(failure, { fallback: t('staff.role.edit.refused') })),
      },
    );
  }

  if (editing) {
    return (
      <Section title={t('staff.role.permissions')} description={t('staff.role.edit.hint')}>
        {error !== undefined ? (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        ) : null}
        <PermissionPicker catalogue={catalogue} selected={selected} onToggle={toggle} />
        <div className="console-form__actions console-form__actions--end">
          <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" variant="primary" busy={update.isPending} onClick={save}>
            {t('staff.role.edit.save')}
          </Button>
        </div>
      </Section>
    );
  }

  return (
    <Section
      title={t('staff.role.permissions')}
      description={t('staff.role.permissionsHint')}
      actions={canEdit ? <Button variant="ghost" size="sm" onClick={start}>{t('staff.role.edit.action')}</Button> : undefined}
    >
      {role.permissions.length === 0 ? (
        <p className="console-subtle">{t('staff.role.noPermissions')}</p>
      ) : (
        <ul className="console-perm-groups" aria-label={t('staff.role.permissionsLabel')}>
          {groupPermissions(role.permissions).map((group) => (
            <li key={group.resource} className="console-perm-group">
              <span className="console-perm-group__resource">{group.label}</span>
              <ul className="console-perm-group__actions">
                {group.actions.map((action) => (
                  <li key={action.slug}>
                    <Chip>{action.label}</Chip>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/**
 * Delete a custom role (F-IAM-11). Deleting one forgives nothing but changes who may do what, so it asks
 * to confirm first; the server refuses (409) a role a member still holds, and that comes back here.
 */
function DeleteRoleSection({ role, onDeleted }: { readonly role: Role; readonly onDeleted: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const scope = useScopeIds();
  const remove = useDeleteRole(scope.organizationId);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  function del(): void {
    setError(undefined);
    remove.mutate(role.name, {
      onSuccess: () => {
        show({ tone: 'ok', title: t('staff.role.delete.deleted', { name: role.name }) });
        onDeleted();
      },
      onError: (caught) => setError(problemCode(caught) === PROBLEM.conflict ? t('staff.role.delete.inUse') : problem.text(caught, { fallback: t('staff.role.delete.refused') })),
    });
  }

  return (
    <Section title={t('staff.role.delete.title')} description={t('staff.role.delete.hint')}>
      {error !== undefined ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      {confirming ? (
        <div className="console-form__actions">
          <Button type="button" variant="ghost" onClick={() => setConfirming(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" variant="danger" busy={remove.isPending} onClick={del}>
            {t('staff.role.delete.confirm')}
          </Button>
        </div>
      ) : (
        <Button type="button" variant="danger" onClick={() => setConfirming(true)}>
          {t('staff.role.delete.action')}
        </Button>
      )}
    </Section>
  );
}

/** A minor-units amount as a plain major-unit decimal for an input, by integer math (no float). */
function minorToMajor(money: Money): string {
  const digits = minorUnitDigits(money.currency);
  if (digits === 0) return String(money.amount);
  const factor = 10 ** digits;
  const whole = Math.trunc(money.amount / factor);
  const fraction = Math.abs(money.amount % factor);
  return `${whole}.${String(fraction).padStart(digits, '0')}`;
}

/** The limits edited one value at a time; the comp limit has its own editor above them. */
type LimitKey = 'manualDiscountMaxBps' | 'refundApprovalAmount' | 'walletAdjustMaxAmount';

/** The limits a role carries besides comp, in the order the dialog shows them (spec/07 §6.4). */
const LIMIT_KEYS: readonly LimitKey[] = ['manualDiscountMaxBps', 'refundApprovalAmount', 'walletAdjustMaxAmount'];

/**
 * A role's limits (spec/07 §6.4), each a value changed where it is read — click, type, Enter — rather
 * than a form of five fields and a Save (BRAND.md §6.1). A holder without `members:roles` reads the same
 * rows, not editable. Each save sends the whole limits object, as the endpoint takes it, with only the
 * one value changed; money is parsed to integer minor units by the shared `moneyFrom`.
 */
function LimitsEditor({ role, currency, canEdit }: { readonly role: Role; readonly currency: string; readonly canEdit: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const format = useVenueFormatter();
  const scope = useScopeIds();
  const access = useAccess(scope.access);
  const change = useChangeRoleLimits(scope.organizationId);
  const toast = useToast();
  // The dialog holds the role it was opened with; the limits it shows follow each save the server accepts.
  const [limits, setLimits] = useState<RoleLimits>(role.limits);

  const label: Record<LimitKey, string> = {
    manualDiscountMaxBps: t('staff.role.limits.discountPercent'),
    refundApprovalAmount: t('staff.role.limits.refundAmount', { currency }),
    walletAdjustMaxAmount: t('staff.role.limits.walletAmount', { currency }),
  };

  const noLimit = <span className="console-subtle">{t('staff.role.limits.noLimit')}</span>;

  function display(key: LimitKey): ReactNode {
    switch (key) {
      case 'manualDiscountMaxBps':
        return limits.manualDiscountMaxBps === null ? noLimit : `${limits.manualDiscountMaxBps / 100}%`;
      default: {
        const amount = limits[key];
        return amount === null ? noLimit : format.money(amount);
      }
    }
  }

  function draft(key: LimitKey): string {
    switch (key) {
      case 'manualDiscountMaxBps':
        return limits.manualDiscountMaxBps === null ? '' : String(limits.manualDiscountMaxBps / 100);
      default: {
        const amount = limits[key];
        return amount === null ? '' : minorToMajor(amount);
      }
    }
  }

  /** The typed text as this limit's value, or the reason it is not one. Blank is no limit. */
  function parse(key: LimitKey, text: string): { readonly value: RoleLimits[LimitKey] } | { readonly error: string } {
    const trimmed = text.trim();
    switch (key) {
      case 'manualDiscountMaxBps': {
        if (trimmed === '') return { value: null };
        const percent = Number(trimmed);
        if (!Number.isFinite(percent) || percent < 0 || percent > 100) return { error: t('staff.role.limits.badPercent') };
        return { value: Math.round(percent * 100) };
      }
      default: {
        if (trimmed === '') return { value: null };
        const amount = moneyFrom(trimmed, currency);
        return amount === null ? { error: t('staff.role.limits.badAmount') } : { value: amount };
      }
    }
  }

  async function save(key: LimitKey, text: string): Promise<string | undefined> {
    const parsed = parse(key, text);
    if ('error' in parsed) return parsed.error;
    const next = { ...limits, [key]: parsed.value } as RoleLimits;
    try {
      await change.mutateAsync({ role: role.name, limits: next });
    } catch {
      return t('staff.role.limits.failed');
    }
    setLimits(next);
    toast.show({ title: t('staff.role.limits.saved'), tone: 'ok' });
    return undefined;
  }

  const comp: CompValues = {
    compUnlimited: limits.compUnlimited ?? false,
    compMaxMinutesPerAction: limits.compMaxMinutesPerAction,
    compMaxMinutesPerShift: limits.compMaxMinutesPerShift,
  };

  async function saveComp(values: CompValues | null): Promise<boolean> {
    if (values === null) return false;
    const next: RoleLimits = { ...limits, ...values };
    try {
      await change.mutateAsync({ role: role.name, limits: next });
    } catch (error) {
      toast.show({ title: problem.text(error, { fallback: t('staff.comp.failed') }), tone: 'danger' });
      return false;
    }
    setLimits(next);
    toast.show({ title: t('staff.comp.saved'), tone: 'ok' });
    return true;
  }

  return (
    <>
    <Section title={t('staff.comp.title')} description={t('staff.comp.roleHint')}>
      <CompLimitEditor
        value={comp}
        forMember={false}
        canEdit={canEdit}
        canGiveUnlimited={access.role === 'owner'}
        busy={change.isPending}
        onSave={saveComp}
      />
    </Section>
    <Section title={t('staff.role.limits.title')} description={t('staff.role.limits.hint')} body="rows">
      {LIMIT_KEYS.map((key) => (
          <SettingRow
            key={key}
            label={label[key]}
            control={
              canEdit ? (
                <InlineEdit
                  label={label[key]}
                  display={display(key)}
                  draft={draft(key)}
                  placeholder={t('staff.role.limits.noLimit')}
                  inputMode="decimal"
                  onSave={(text) => save(key, text)}
                />
              ) : (
                <span className="console-num">{display(key)}</span>
              )
            }
          />
      ))}
    </Section>
    </>
  );
}
